import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/utils/analytics";
import api from "@/lib/api";
import SEO from "@/components/seo/SEO";

/**
 * Landing page after a real Mollie checkout redirect. Polls the payment status until it's
 * confirmed (webhook usually beats the redirect, but we poll for up to 60s just in case Mollie's
 * webhook is slow in real-world production conditions), then automatically resumes whatever the
 * user was doing (spend tokens for an order, unlock a download...) before sending them back to
 * the right place - no manual step required. If we still don't have a definitive answer after
 * 60s, we NEVER show a dead-end error (the payment might just still be confirming) - instead we
 * offer a "check again" button so the user never needs to start a brand new purchase.
 */
export default function PaymentReturn() {
  const { t } = useI18n();
  const { refetch } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("waiting"); // waiting | success | error | still_pending
  const [errorReason, setErrorReason] = useState(null); // failed | canceled | expired | null
  const paymentId = params.get("payment_id");
  const cancelledRef = useRef(false);

  const poll = async (attempt = 0) => {
    try {
      const { data } = await api.get(`/payments/${paymentId}`);
      if (data.status === "paid") {
        await refetch();
        // Funnel visibility (Itération 39 audit): fires only once the payment is confirmed
        // "paid" (never on mere webhook delivery) - see index.html gtag config.
        trackEvent("purchase", { currency: "EUR", value: data.amount, transaction_id: paymentId });
        if (data.resume_order_id && data.resume_action === "spend") {
          await api.post("/wallet/spend", { order_id: data.resume_order_id });
          await refetch();
          if (!cancelledRef.current) {
            setState("success");
            setTimeout(() => navigate(`/orders/${data.resume_order_id}`), 1200);
          }
          return;
        }
        if (data.resume_order_id && data.resume_action === "unlock_download") {
          await api.post("/wallet/spend-unlock", { order_id: data.resume_order_id });
          await refetch();
          if (!cancelledRef.current) {
            setState("success");
            setTimeout(() => navigate(`/orders/${data.resume_order_id}`), 1200);
          }
          return;
        }
        if (!cancelledRef.current) {
          setState("success");
          setTimeout(() => navigate("/dashboard"), 1200);
        }
        return;
      }
      if (["failed", "canceled", "expired"].includes(data.status)) {
        // Funnel visibility (Itération 49 audit): this branch was previously silent for GA4 -
        // the "purchase" event above only ever fired on success, so failed/expired/canceled
        // web checkouts (the vast majority of Mollie abandonment) were completely invisible in
        // Analytics. Now explicitly tracked with the exact reason.
        trackEvent("payment_failed", { currency: "EUR", value: data.amount, reason: data.status });
        if (!cancelledRef.current) {
          setErrorReason(data.status);
          setState("error");
        }
        return;
      }
      // Still "open"/"pending"/"authorized": Mollie's webhook can occasionally take longer
      // than a few seconds to reach us (real-world network latency), so we poll generously
      // (up to 60s) before ever telling the user something went wrong - never a dead-end.
      if (attempt < 20 && !cancelledRef.current) {
        setTimeout(() => poll(attempt + 1), 3000);
      } else if (!cancelledRef.current) {
        setState("still_pending");
      }
    } catch {
      if (!cancelledRef.current) setState("still_pending");
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    if (paymentId) poll(0);
    else setState("error");
    return () => { cancelledRef.current = true; };
  }, [paymentId]);

  const checkAgain = () => {
    setState("waiting");
    poll(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-md px-4 py-24 text-center">
      <SEO title="Confirmation de paiement | Kalypso Music" description="Confirmation du paiement de votre chanson personnalisée Kalypso Music." path="/payment/return" noindex />
        {state === "waiting" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-6 font-heading text-xl font-medium">{t("paymentReturn.title")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("paymentReturn.waiting")}</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h1 className="mt-6 font-heading text-xl font-medium">{t("paymentReturn.success")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("paymentReturn.resuming")}</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-destructive" />
            <h1 className="mt-6 font-heading text-xl font-medium">{t("paymentReturn.error")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {errorReason ? t(`paymentReturn.reason_${errorReason}`) : null}
            </p>
            <button onClick={() => navigate("/dashboard")} className="mt-4 text-sm text-primary underline">
              {t("paymentReturn.goToWallet")}
            </button>
          </>
        )}
        {state === "still_pending" && (
          <>
            <HelpCircle className="mx-auto h-10 w-10 text-amber-500" />
            <h1 className="mt-6 font-heading text-xl font-medium">{t("paymentReturn.stillPendingTitle")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("paymentReturn.stillPendingDesc")}</p>
            <div className="mt-4 flex flex-col items-center gap-2">
              <button data-testid="payment-return-check-again-btn" onClick={checkAgain} className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground">
                {t("paymentReturn.checkAgain")}
              </button>
              <button onClick={() => navigate("/dashboard")} className="text-sm text-primary underline">
                {t("paymentReturn.goToWallet")}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
